import { Box, IconButton, InputAdornment, Typography } from "@mui/material"
import { AddOutlined, DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { DEFAULT_TIER_UOM, MAX_TIER_ROWS, MIN_TIER_ROWS } from "./constants"
import type { PricingTierFormValue, SpecialPricingFormValues } from "./schemas"

import { Button } from "@/components/common/Button"
import { FormCurrencyInput, FormNumericField } from "@/components/UiKit"

const TIER_QTY_END_ADORNMENT = (
  <InputAdornment position='end'>
    <Typography className='text-sm text-gray-500'>lb</Typography>
  </InputAdornment>
)

interface PricingTierTableProps {
  productId: number
}

export const PricingTierTable: FC<PricingTierTableProps> = ({ productId }) => {
  const { control } = useFormContext<SpecialPricingFormValues>()
  const { fields, append, remove } = useFieldArray<SpecialPricingFormValues, "tiers">({ control, name: "tiers" })

  const handleTierAdd = () => {
    const newTier: PricingTierFormValue = { qty: null, uom: DEFAULT_TIER_UOM, price: "" }
    append(newTier)
  }

  const handleTierRemove = (index: number) => {
    remove(index)
  }

  return (
    <Box className='flex flex-col gap-2'>
      <Typography className='text-sm font-semibold text-gray-800'>Tiers</Typography>
      {fields.map((field, index) => (
        <Box key={field.id} className='flex items-start gap-2'>
          <Box className='flex-1'>
            <FormNumericField
              control={control}
              name={`tiers.${index}.qty`}
              placeholder='Qty'
              slotProps={{ input: { endAdornment: TIER_QTY_END_ADORNMENT } }}
              data-testid={`sales.product-pricing-form.tier-qty-field-${productId}-${index}`}
            />
          </Box>
          <Box className='flex-1'>
            <FormCurrencyInput
              control={control}
              name={`tiers.${index}.price`}
              placeholder='Price'
              data-testid={`sales.product-pricing-form.tier-price-field-${productId}-${index}`}
            />
          </Box>
          <IconButton
            size='small'
            disabled={fields.length <= MIN_TIER_ROWS}
            onClick={() => handleTierRemove(index)}
            aria-label='Remove tier'
            data-testid={`sales.product-pricing-form.tier-remove-button-${productId}-${index}`}
          >
            <DeleteOutlined />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button
          appearance='outlined'
          size='small'
          startIcon={<AddOutlined />}
          onClick={handleTierAdd}
          disabled={fields.length >= MAX_TIER_ROWS}
          data-testid={`sales.product-pricing-form.add-tier-button-${productId}`}
        >
          Add Tier
        </Button>
      </Box>
    </Box>
  )
}
